import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller,useForm } from 'react-hook-form';

import {
	ActionItemStatus,
	markInTransitToFulfillmentCenterFormSchema,
} from '@ekhodealer/ekho-common';
import {
	Button,
	DateInput,
	SkeletonRect,
	Stack,
	Text,
	TextInput,
	toast,
} from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { dateValueToTimestamp,timestampToDateValue } from '@common/lib/time';

import { useGetMarkInTransitToFulfillmentCenter } from '../../../api/progress/useGetMarkInTransitToFulfillmentCenter';
import { useMarkInTransitToFulfillmentCenter } from '../../../api/progress/useMarkInTransitToFulfillmentCenter';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface MarkInTransitToFulfillmentCenterDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	transitToFulfillmentCenterDetails: {
		estimatedDeliveryDateToFulfillmentCenter: number;
		vehicleTrackingReference?: string;
		contactNumber?: string;
		companyName?: string;
	};
}

/**
 * MarkInTransitToFulfillmentCenterDetail form component for marking a vehicle as in transit to fulfillment center.
 * Displays a form with estimated date (required), tracking reference, contact number, and company name (optional).
 */
export const MarkInTransitToFulfillmentCenterDetail = ({
	actionItem,
}: MarkInTransitToFulfillmentCenterDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const markInTransitMutation = useMarkInTransitToFulfillmentCenter(orderId);
	const { data: existingTransitData, isLoading: isLoadingTransitData } =
		useGetMarkInTransitToFulfillmentCenter(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(markInTransitToFulfillmentCenterFormSchema),
		defaultValues: {
			transitToFulfillmentCenterDetails: {
				estimatedDeliveryDateToFulfillmentCenter: undefined as unknown as number,
				vehicleTrackingReference: '',
				contactNumber: '',
				companyName: '',
			},
		},
	});

	// Pre-populate form with existing transit data
	useEffect(() => {
		const details = existingTransitData?.transitToFulfillmentCenterDetails;
		if (details) {
			reset({
				transitToFulfillmentCenterDetails: {
					estimatedDeliveryDateToFulfillmentCenter:
						details.estimatedDeliveryDateToFulfillmentCenter,
					vehicleTrackingReference: details.vehicleTrackingReference || '',
					contactNumber: details.contactNumber || '',
					companyName: details.companyName || '',
				},
			});
		}
	}, [existingTransitData, reset]);

	const submitHandler = handleSubmit(async (data: FormData) => {
		try {
			await markInTransitMutation.mutateAsync({
				transitToFulfillmentCenterDetails: {
					estimatedDeliveryDateToFulfillmentCenter:
						data.transitToFulfillmentCenterDetails
							.estimatedDeliveryDateToFulfillmentCenter,
					vehicleTrackingReference:
						data.transitToFulfillmentCenterDetails.vehicleTrackingReference?.trim() ||
						undefined,
					contactNumber:
						data.transitToFulfillmentCenterDetails.contactNumber?.trim() || undefined,
					companyName:
						data.transitToFulfillmentCenterDetails.companyName?.trim() || undefined,
				},
			});
			toast({
				message: 'Vehicle marked as in transit to fulfillment center successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			const errorMessage =
				err instanceof Error
					? err.message
					: 'Failed to mark vehicle as in transit to fulfillment center';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Enter the estimated delivery date and optional transit details. Note: an email will be sent to the buyer communicating the updated status.'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{isLoadingTransitData ? (
					<SkeletonRect width='100%' height={200} />
				) : (
					<>
						{/* ESTIMATED DELIVERY DATE */}
						<Controller
							name='transitToFulfillmentCenterDetails.estimatedDeliveryDateToFulfillmentCenter'
							control={control}
							render={({ field: { onChange, value } }) => (
								<DateInput
									label='Estimated delivery date '
									isRequired
									isReadOnly={
										isSubmitting ||
										markInTransitMutation.isPending ||
										actionItem.status === ActionItemStatus.COMPLETE
									}
									variant='outline'
									size='large'
									value={timestampToDateValue(value)}
									onChange={(val) => {
										const timestamp = dateValueToTimestamp(val);
										onChange(timestamp);
									}}
									hasError={
										errors.transitToFulfillmentCenterDetails
											?.estimatedDeliveryDateToFulfillmentCenter?.message
									}
								/>
							)}
						/>

						{/* TRACKING REFERENCE */}
						<Controller
							name='transitToFulfillmentCenterDetails.vehicleTrackingReference'
							control={control}
							render={({ field: { onChange, onBlur, value } }) => (
								<Stack itemsSpacing='4'>
									<Text size='footnote'>Tracking reference (optional)</Text>
									<TextInput
										placeholder='Enter tracking reference'
										value={value}
										onChange={onChange}
										onBlur={onBlur}
										size='large'
										isDisabled={
											isSubmitting ||
											markInTransitMutation.isPending ||
											actionItem.status === ActionItemStatus.COMPLETE
										}
									/>
								</Stack>
							)}
						/>

						{/* CONTACT NUMBER */}
						<Controller
							name='transitToFulfillmentCenterDetails.contactNumber'
							control={control}
							render={({ field: { onChange, onBlur, value } }) => (
								<Stack itemsSpacing='4'>
									<Text size='footnote'>Contact number (optional)</Text>
									<TextInput
										placeholder='Enter contact number'
										value={value}
										onChange={onChange}
										onBlur={onBlur}
										size='large'
										isDisabled={
											isSubmitting ||
											markInTransitMutation.isPending ||
											actionItem.status === ActionItemStatus.COMPLETE
										}
									/>
								</Stack>
							)}
						/>

						{/* COMPANY NAME */}
						<Controller
							name='transitToFulfillmentCenterDetails.companyName'
							control={control}
							render={({ field: { onChange, onBlur, value } }) => (
								<Stack itemsSpacing='4'>
									<Text size='footnote'>Company name (optional)</Text>
									<TextInput
										placeholder='Enter company name'
										value={value}
										onChange={onChange}
										onBlur={onBlur}
										size='large'
										isDisabled={
											isSubmitting ||
											markInTransitMutation.isPending ||
											actionItem.status === ActionItemStatus.COMPLETE
										}
									/>
								</Stack>
							)}
						/>
					</>
				)}

				{actionItem.status !== ActionItemStatus.COMPLETE && (
					<Button
						size='large'
						isFullWidth
						onPress={() => {
							submitHandler();
						}}
						isLoading={isSubmitting || markInTransitMutation.isPending}
						isDisabled={isLoadingTransitData}>
						{actionItem.config.buttonTitle || 'Mark in transit'}
					</Button>
				)}
			</Stack>
		</Stack>
	);
};
